import { extractId } from "@repo/shopify/src/utils";
import i18next from "i18next";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Button from "@components/atoms/Button";
import Icon from "@components/atoms/Icon";
import Checkbox from "@components/atoms/Input/Checkbox";

import useLayout from "@hooks/reducerHooks/useLayout";
import useUser from "@hooks/reducerHooks/useUser";
import useCertification from "@hooks/utilsHooks/useCertification";

import { IconName } from "@const/enum";
import { URLS } from "@const/urls";

import { NOTIFICATIONS } from "@utils/notifications";

import { fetchUserVerify } from "@api/lamdaApi";

interface IProps {
  redirectUrl: string;
  onComplete?: () => void;
}

function VerifyAccount({ redirectUrl, onComplete }: IProps) {
  const navigate = useNavigate();

  const { updateUser, fetchUser, accessToken, customer } = useUser();
  const { closeLoginModal, openToast, openModal, closeModal } = useLayout();
  const { onOpenCertificationPopup } = useCertification();

  const [privacyAgreement, setPrivacyAgreement] = useState(false); // 개인정보 처리방침 및 이용약관 동의 체크여부
  const [newsletterAgreement, setNewsletterAgreement] = useState(false); // 개인정보 처리방침 및 이용약관 동의 체크여부
  const [isProgressing, setIsProgressing] = useState(false); // 본인인증 API 진행중

  const { t } = useTranslation();

  const onCertification = async () => {
    if (isProgressing) return;

    setIsProgressing(true);

    const verifyData = await onOpenCertificationPopup();
    if (!verifyData) return;

    const { phone, name, birthday, gender } = verifyData;

    /** 본인인증 shopify 유저 정보 업데이트 및 로그인 처리 */
    const param = {
      phone,
      lastName: name.slice(0, 1),
      firstName: name.slice(1),
      birth: birthday,
      gender: gender === "male" ? "남" : "여",
      acceptsMarketing: newsletterAgreement,
      customerId: extractId(customer.id),
    };

    const response = await fetchUserVerify(param);
    if (response) {
      // 다른 계정에 등록된 전화번호인 경우
      if (response?.email) {
        closeLoginModal();

        openModal({
          title: "Verification Error",
          description: response?.social
            ? i18next.t("toast.account.warn.phone_verify_alert_with_social", {
                email: response.email,
                social: response?.social,
              })
            : i18next.t("toast.account.warn.phone_verify_alert", {
                email: response.email,
              }),
          confirmButton: {
            label: "Okay",
            onClick: () => {
              closeModal();
            },
          },
        });
        setIsProgressing(false);
        return;
      }

      if (response.data?.customerUpdate?.userErrors?.length) {
        response.data?.customerUpdate?.userErrors.forEach((error: any) => {
          openToast(error.message.includes("taken") ? NOTIFICATIONS.WARN_PHONE_VERIFY : NOTIFICATIONS.ERROR_DEFAULT);
        });

        setIsProgressing(false);
        return;
      }

      updateUser({ customer, accessToken, ...customer, ...param });
      await fetchUser({ variables: { customerAccessToken: accessToken } });

      setIsProgressing(false);
      openToast(NOTIFICATIONS.SUCCESS_VERIFY);
      closeLoginModal();
      onComplete?.();
      navigate(redirectUrl || URLS.ACCOUNT_ORDER_HISTORY, { replace: true });
    }

    setIsProgressing(false);
  };

  return (
    <div className="flex flex-col items-center">
      <div className="whitespace-pre pb-5 text-center text-black">{t("login.auth_ask")}</div>
      <Button
        className="mb-5 w-full"
        disabled={!privacyAgreement}
        isLoading={isProgressing}
        onClick={onCertification}
        styleType="primary"
        variant="social"
      >
        <span className="flex w-full items-center justify-center gap-[8px]">
          <Icon alt="lock" className="size-[14px]" name={IconName.Lock} />
          <span>{t("login.auth")}</span>
        </span>
      </Button>
      <div className="flex flex-col items-center gap-2.5">
        <Checkbox
          checked={privacyAgreement}
          className="text-black"
          label={t("login.personal_info")}
          onChange={(e) => {
            setPrivacyAgreement(e.target.checked);
          }}
        />
        <Checkbox
          checked={newsletterAgreement}
          className="text-black"
          label={t("login.marketing_subscribe")}
          onChange={(e) => {
            setNewsletterAgreement(e.target.checked);
          }}
        />
      </div>
    </div>
  );
}

export default VerifyAccount;
